import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { theme } from "../theme"; // Assuming you have a theme with color variants
import { rgba } from "polished";
import { Flex } from "./layout";

const AvatarStyle = styled.div((props) => {
  const {
    size = "md",
    shape = "circle",
    variant = "filled",
    sx = {},
    src,
    color = "default",
  } = props;

  let avatarSize, avatarPadding;
  switch (size) {
    case "xs":
      avatarSize = "2.125rem";
      avatarPadding = "0.5rem";
      break;
    case "sm":
      avatarSize = "2.5rem";
      avatarPadding = "0.5rem";
      break;
    case "lg":
      avatarSize = "6.25rem";
      avatarPadding = "1.25rem";
      break;
    default:
      avatarSize = "3.75rem";
      avatarPadding = "0.9375rem";
  }

  // Determine background color based on the variant
  const backgroundColor = (() => {
    switch (variant) {
      case "filled":
        return src
          ? "transparent" // Keep it transparent if an image is present
          : rgba(theme.colors[color]?.light, 0.15) ||
              theme.colors.default.light;
      case "contained":
        return src
          ? "transparent" // Keep it transparent if an image is present
          : theme.colors[color]?.main || theme.colors.default.main; // Lightened background color for filled/contained
      case "outlined":
        return "transparent"; // Outlined variant has no background color
      default:
        return theme.colors.default.main;
    }
  })();

  const borderColor =
    variant === "outlined" ? theme.colors[color]?.main : "transparent";

  return {
    width: avatarSize,
    minWidth: avatarSize,
    height: avatarSize,
    borderRadius: shape === "circle" ? "50%" : "0.5rem",
    backgroundColor,
    backgroundImage: src ? `url(${src})` : "none",
    backgroundSize: "cover",
    backgroundPosition: "center",
    fontWeight: 550,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize:
      size === "xs" ? "0.75rem" : size === "sm" ? "0.875rem" : size === "lg" ? "1.5rem" : "1.125rem",
    color: src
      ? theme.colors.default.dark
      : variant === "contained"
        ? theme.colors.light.light
        : theme.colors[color]?.dark, // Adjust text color for contrast
    textTransform: "uppercase",
    border: variant === "outlined" ? `2px solid ${borderColor}` : "none", // Outlined variant
    boxShadow:
      variant === "contained"
        ? `0 2px 4px ${rgba(theme.colors[color]?.main, 0.2)}`
        : "none", // Optional shadow for contained variant
    ...sx,

    "& .avatarIcon": {
      width: "100%",
      height: "100%",
      padding: avatarPadding,
    },
  };
});

export const Avatar = ({
  size,
  shape,
  src,
  icon,
  initials,
  color,
  variant,
  className,
  sx,
}) => {
  return (
    <AvatarStyle
      size={size}
      shape={shape}
      src={src}
      icon={icon}
      initials={initials}
      color={color}
      variant={variant}
      className={className}
      sx={sx}
    >
      {!src && initials}
      {icon && <Flex className="avatarIcon">{icon}</Flex>}
    </AvatarStyle>
  );
};

// PropTypes for the Avatar component
Avatar.propTypes = {
  /** Size of the avatar */
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  /** Shape of the avatar (circle or square) */
  shape: PropTypes.oneOf(["circle", "square"]),
  /** Image source for the avatar */
  src: PropTypes.string,
  /** Initials to display if no image is provided */
  initials: PropTypes.string,
  /** Color variant for the avatar background */
  color: PropTypes.oneOf([
    "default",
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "light",
    "dark",
  ]),
  /** Variant of the avatar (filled, outlined, or contained) */
  variant: PropTypes.oneOf(["filled", "outlined", "contained"]),
  /** Additional CSS class name for custom styling */
  className: PropTypes.string,
  /** Custom styles using sx prop */
  sx: PropTypes.object,
};

// Default props for Avatar component
Avatar.defaultProps = {
  size: "md",
  shape: "circle",
  src: "", // Default to an empty string, optional for initials
  initials: "", // Default initials if no src is provided
  color: "default",
  variant: "filled",
  sx: {},
};
